import { z } from 'zod';
import { JOB_PRIORITIES, JOB_SOURCES, JOB_STATUSES, SERVICE_TYPES, WEIGHT_UNITS, } from '../constants.js';
import { addressSchema, currencySchema, idSchema, isoDateTime, pageQuerySchema, paginated, weightKgSchema, } from './common.js';
export const jobStatusSchema = z.enum(JOB_STATUSES);
export const jobPrioritySchema = z.enum(JOB_PRIORITIES);
export const serviceTypeSchema = z.enum(SERVICE_TYPES);
export const jobSourceSchema = z.enum(JOB_SOURCES);
export const weightUnitSchema = z.enum(WEIGHT_UNITS);
/* --------------------------------- Job ---------------------------------- */
export const jobSchema = z.object({
    id: idSchema,
    source: jobSourceSchema,
    /** Shiptheory/Company XYZ reference — unique per source (dedupe key). */
    externalRef: z.string().min(1).max(120),
    /** Idempotency/correlation id echoed back on intake. */
    correlationId: z.string().min(1).max(120),
    clientId: idSchema,
    status: jobStatusSchema,
    priority: jobPrioritySchema,
    serviceType: serviceTypeSchema,
    collectionAddress: addressSchema,
    deliveryAddress: addressSchema,
    deadlineAt: isoDateTime.nullish(),
    timeWindowStart: isoDateTime.nullish(),
    timeWindowEnd: isoDateTime.nullish(),
    description: z.string().max(2000).nullish(),
    parcels: z.number().int().min(1).max(9999).default(1),
    weightKg: weightKgSchema,
    /** Original weight unit as received (weightKg is always canonical kg). */
    weightUnit: weightUnitSchema.default('kg'),
    handlingInstructions: z.string().max(4000).nullish(),
    internalNotes: z.string().max(4000).nullish(),
    /* Assignment (current — full history in job_assignments) */
    driverId: idSchema.nullish(),
    vehicleId: idSchema.nullish(),
    /* Pricing */
    priceRuleId: idSchema.nullish(),
    chargeSubtotal: z.number().finite().nullish(),
    chargeTax: z.number().finite().nullish(),
    chargeTotal: z.number().finite().nullish(),
    currency: currencySchema.default('GBP'),
    priceOverrideAmount: z.number().finite().nullish(),
    priceOverrideReason: z.string().max(1000).nullish(),
    pricedAt: isoDateTime.nullish(),
    manualPricing: z.boolean().default(false),
    /* Evidence & finance linkage */
    podRequired: z.boolean().default(true),
    invoiceId: idSchema.nullish(),
    /* Timeline */
    receivedAt: isoDateTime,
    allocatedAt: isoDateTime.nullish(),
    acceptedAt: isoDateTime.nullish(),
    startedAt: isoDateTime.nullish(),
    completedAt: isoDateTime.nullish(),
    deliveredAt: isoDateTime.nullish(),
    createdAt: isoDateTime,
    updatedAt: isoDateTime,
});
/* --------------------------- create / update ----------------------------- */
export const jobCreateSchema = z
    .object({
    source: jobSourceSchema.default('manual'),
    externalRef: z.string().min(1).max(120),
    correlationId: z.string().min(1).max(120).optional(),
    clientId: idSchema,
    priority: jobPrioritySchema.default('normal'),
    serviceType: serviceTypeSchema.default('standard'),
    collectionAddress: addressSchema,
    deliveryAddress: addressSchema,
    deadlineAt: isoDateTime.nullish(),
    timeWindowStart: isoDateTime.nullish(),
    timeWindowEnd: isoDateTime.nullish(),
    description: z.string().max(2000).nullish(),
    parcels: z.number().int().min(1).max(9999).default(1),
    weightKg: weightKgSchema,
    weightUnit: weightUnitSchema.default('kg'),
    handlingInstructions: z.string().max(4000).nullish(),
    internalNotes: z.string().max(4000).nullish(),
    podRequired: z.boolean().default(true),
})
    .refine((v) => !(v.deadlineAt && v.timeWindowStart && v.timeWindowEnd && v.timeWindowEnd < v.timeWindowStart), {
    message: 'timeWindowEnd must be after timeWindowStart',
    path: ['timeWindowEnd'],
});
export const jobUpdateSchema = z
    .object({
    priority: jobPrioritySchema.optional(),
    serviceType: serviceTypeSchema.optional(),
    deadlineAt: isoDateTime.nullish(),
    timeWindowStart: isoDateTime.nullish(),
    timeWindowEnd: isoDateTime.nullish(),
    description: z.string().max(2000).nullish(),
    parcels: z.number().int().min(1).max(9999).optional(),
    weightKg: weightKgSchema.optional(),
    collectionAddress: addressSchema.optional(),
    deliveryAddress: addressSchema.optional(),
    handlingInstructions: z.string().max(4000).nullish(),
    internalNotes: z.string().max(4000).nullish(),
    clientId: idSchema.optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'No fields to update' });
/* -------------------------------- filters ------------------------------- */
export const jobListQuerySchema = pageQuerySchema.extend({
    status: z.union([jobStatusSchema, z.array(jobStatusSchema)]).optional(),
    priority: z.union([jobPrioritySchema, z.array(jobPrioritySchema)]).optional(),
    clientId: idSchema.optional(),
    driverId: idSchema.optional(),
    vehicleId: idSchema.optional(),
    source: jobSourceSchema.optional(),
    serviceType: serviceTypeSchema.optional(),
    /** Free text over externalRef, correlationId, addresses, client name. */
    search: z.string().max(200).optional(),
    deadlineFrom: isoDateTime.optional(),
    deadlineTo: isoDateTime.optional(),
    /** Sort: deadline (default) | priority | created | status | weight | updated */
    sort: z.enum(['deadline', 'priority', 'created', 'updated', 'status', 'weight']).default('deadline'),
});
export function asArray(v) {
    if (v === undefined)
        return undefined;
    return Array.isArray(v) ? v : [v];
}
export const jobListSchema = paginated(jobSchema);
/* --------------------------- status transitions -------------------------- */
export const statusTransitionSchema = z.object({
    to: jobStatusSchema,
    reason: z.string().max(1000).nullish(),
    /** Driver-side proof reference when completing. */
    proofId: idSchema.nullish(),
    metadata: z.record(z.string(), z.unknown()).optional(),
});
export const jobStatusHistoryEntrySchema = z.object({
    id: idSchema,
    jobId: idSchema,
    fromStatus: jobStatusSchema.nullish(),
    toStatus: jobStatusSchema,
    actorUserId: idSchema.nullish(),
    actorRole: z.string().max(40).nullish(),
    reason: z.string().max(1000).nullish(),
    createdAt: isoDateTime,
});
/* ------------------------------- allocation ----------------------------- */
export const allocateSchema = z.object({
    driverId: idSchema,
    vehicleId: idSchema,
    /** Required when the assignment exceeds vehicle capacity. */
    capacityOverride: z
        .object({
        acknowledged: z.literal(true),
        reason: z.string().trim().min(10).max(1000),
    })
        .nullish(),
    note: z.string().max(1000).nullish(),
});
export const releaseSchema = z.object({
    reason: z.string().trim().min(3).max(1000),
});
export const assignmentSchema = z.object({
    id: idSchema,
    jobId: idSchema,
    driverId: idSchema,
    vehicleId: idSchema,
    assignedBy: idSchema,
    assignedAt: isoDateTime,
    releasedAt: isoDateTime.nullish(),
    releasedBy: idSchema.nullish(),
    reassignmentOf: idSchema.nullish(),
    reason: z.string().max(1000).nullish(),
    overrideCapacity: z.boolean().default(false),
    overrideReason: z.string().max(1000).nullish(),
    isCurrent: z.boolean(),
});
/** Response of the pre-dispatch capacity/conflict check endpoint. */
export const allocationPreviewSchema = z.object({
    jobId: idSchema,
    driverId: idSchema,
    vehicleId: idSchema,
    capacity: z.object({
        allowed: z.boolean(),
        capacityKg: z.number(),
        assignedKg: z.number(),
        additionalKg: z.number(),
        projectedKg: z.number(),
        remainingKg: z.number(),
        utilisationPct: z.number(),
        requiresOverride: z.boolean(),
        overloadKg: z.number(),
    }),
    conflicts: z.array(z.object({
        type: z.enum(['window_overlap', 'deadline_clash', 'same_day_load']),
        severity: z.enum(['warning', 'blocker']),
        message: z.string(),
        relatedJobId: idSchema,
    })),
    allowed: z.boolean(),
    /** Human-readable blockers (e.g. capacity exceeded, driver inactive). */
    blockers: z.array(z.string()),
});
//# sourceMappingURL=job.js.map